/**
 * Onboarding ved første login (Prismodel 2.0): tre korte trin der forklarer
 * fordelsstigen, betaling og kontakt. Vises når profiles.onboarded_at er
 * null; lukning (uanset hvordan) markerer den som set — den generer aldrig
 * brugeren to gange. Sælgere ser den ikke.
 */
import { useEffect, useState } from "react";
import { CreditCard, Headset, TrendingUp } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/features/auth/AuthContext";
import { cn } from "@/lib/utils";

const STEPS = [
  {
    icon: TrendingUp,
    title: "Din fordelsstige",
    body:
      "Alle køb tæller. Du optjener 2 % cashback fra første ordre, og fra 10.000 kr (ex moms) rykker du op på Bronze med rabat på filament. Stigen fortsætter til Sølv og Guld — du kan altid følge din fremgang på forsiden.",
  },
  {
    icon: CreditCard,
    title: "Nem betaling",
    body:
      "Du betaler med kort via Shopifys sikre checkout — betalingslinket kommer, så snart du afgiver ordren. Fra Sølv-niveau kan faktura med kredit aktiveres.",
  },
  {
    icon: Headset,
    title: "Vi står klar",
    body:
      "Spørgsmål, storkøb eller brug for en fast aftale? Ring +45 21 99 03 07 eller skriv til shop@lab3d.dk — så finder vi den rigtige løsning.",
  },
] as const;

export function OnboardingDialog() {
  const { profile, isSalesperson } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const shouldShow =
    !!profile && !isSalesperson && profile.status === "active" && !profile.onboarded_at;

  useEffect(() => {
    if (shouldShow) setOpen(true);
  }, [shouldShow]);

  const markSeen = () => {
    setOpen(false);
    if (!profile) return;
    void supabase
      .from("profiles")
      .update({ onboarded_at: new Date().toISOString() })
      .eq("user_id", profile.user_id)
      .then(({ error }) => {
        if (error) logger.warn("onboarding_markering_fejlede", { error: error.message });
      });
  };

  if (!shouldShow) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && markSeen()}>
      <DialogContent className="max-w-sm">
        <DialogHeader className="items-center text-center">
          <span className="mb-1 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent">
            <Icon className="h-7 w-7 text-navy" aria-hidden />
          </span>
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-ink-2">
            {current.body}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-1.5 py-1" aria-hidden>
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === step ? "w-6 bg-navy" : "w-1.5 bg-border",
              )}
            />
          ))}
        </div>

        <div className="flex gap-2">
          {step > 0 && (
            <Button variant="outline" className="min-h-11 flex-1" onClick={() => setStep(step - 1)}>
              Tilbage
            </Button>
          )}
          <Button
            className="min-h-11 flex-1"
            onClick={() => (isLast ? markSeen() : setStep(step + 1))}
          >
            {isLast ? "Kom i gang" : "Næste"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
